import { useState } from 'react';
import { allProducts } from '../../../mocks/products';
import ProductModal from './ProductModal';

export default function ProductGrid() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState<typeof allProducts[0] | null>(null);

  const categories = ['All', 'Pulses', 'Spices', 'Rice', 'Maize', 'Coffee', 'Tea'];

  const filteredProducts = selectedCategory === 'All'
    ? allProducts
    : allProducts.filter(product => product.category === selectedCategory);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Category Filter */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-3 scroll-smooth hide-scrollbar">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 sm:px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-emerald-700 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Count */}
        <div className="mb-6 sm:mb-8">
          <p className="text-sm sm:text-base text-gray-600">
            Showing <strong className="text-gray-900">{filteredProducts.length}</strong> {filteredProducts.length === 1 ? 'product' : 'products'}
            {selectedCategory !== 'All' && <span> in <strong className="text-emerald-700">{selectedCategory}</strong></span>}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              onClick={() => setSelectedProduct(product)}
              className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-emerald-200 flex flex-col"
            >
              {/* Product Image */}
              <div className="relative overflow-hidden bg-gray-100 w-full aspect-[4/3]">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-medium text-emerald-700 shadow-sm">
                  {product.category}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4 sm:p-5 flex flex-col flex-grow">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
                  {product.description}
                </p>

                {/* Quick Info */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-box-3-line text-base text-gray-400"></i>
                    <span className="text-xs text-gray-600 truncate">{product.details.packaging.split(',')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-base text-gray-400"></i>
                    <span className="text-xs text-gray-600">{product.details.shelfLife}</span>
                  </div>
                </div>

                {/* Certifications */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                    FSSAI
                  </span>
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                    APEDA
                  </span>
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                    +6 more
                  </span>
                </div>

                {/* View Details Button */}
                <button className="w-full px-4 py-2.5 sm:py-3 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-all whitespace-nowrap cursor-pointer group-hover:shadow-lg">
                  View Details
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Products Found */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-16 sm:py-20">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600 text-base sm:text-lg">No products found in this category.</p>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
